import clsx from "clsx";
import { defaultComposer } from "default-composer";

/**
 * The `repr` function returns the string representation of the type of the
 * argument.
 * @param {any} arg - The parameter "arg" is of type "any", which means it can
 * accept any data type as an argument.
 */
export const repr = (arg: any) => Object.prototype.toString.call(arg);

/**
 * Checks if a given color string represents a CSS variable.
 *
 * @param {string | undefined} color - The color string to check.
 * @returns {boolean} `true` if the color is a CSS variable, `false` otherwise.
 * @example
 * const isCSSVar = isCSSVariable('var(--primary-color)');
 * console.log(isCSSVar); // Output: true
 *
 * @example
 * const notCSSVar = isCSSVariable('#336699');
 * console.log(notCSSVar); // Output: false
 */
export const isCSSVariable = (color?: string): boolean => {
	if (!color) return false;
	return color.startsWith("var(");
};

/**
 * The function checks if the argument is an array.
 * @param {any} arg - The parameter `arg` is of type `any`, which means it can
 * accept any value.
 */
export const isArray = (arg: any): arg is any[] =>
	Array.isArray ? Array.isArray(arg) : repr(arg) === "[object Array]";

/**
 * The function checks if the argument is an object.
 * @param {any} arg - The `arg` parameter is of type `any`, which means it can
 * accept any data type as an argument.
 */
export const isObject = (arg: any) => repr(arg) === "[object Object]";

/**
 * The function checks if the argument is null.
 * @param {any} arg - The parameter `arg` is of type `any`, which means it can
 * accept any type of value.
 */
export const isNull = (arg: any) => repr(arg) === "[object Null]";

/**
 * The function checks if a given argument is undefined.
 * @param {any} arg - The `arg` parameter is of type `any`, which means it can
 * accept any data type.
 * @returns The function `isUndefined` returns a boolean value indicating whether
 * the argument `arg` is undefined.
 */
export const isUndefined = (arg: any) => {
	try {
		return typeof arg === "undefined";
	} catch (e) {
		if (e instanceof ReferenceError) {
			return true;
		}

		throw e;
	}
};

/**
 * The function `isString` checks if the argument is a string.
 * @param {any} arg - The `arg` parameter is of type `any`, which means it can
 * accept any value.
 */
export const isString = (arg: any): arg is string =>
	repr(arg) === "[object String]";

/**
 * The `isEmpty` function checks if a given argument is empty, which includes being
 * undefined, null, an empty string, an empty array, or an object with no keys.
 * @param {any} arg - The parameter `arg` is of type `any`, which means it can
 * accept any data type.
 */
export const isEmpty = (arg: any) =>
	isUndefined(arg) ||
	isNull(arg) ||
	(isString(arg) && arg.length === 0) ||
	(isArray(arg) && arg.length === 0) ||
	(isObject(arg) && Object.keys(arg).length === 0);

/**
 * The `ucFirst` function takes a string as input and returns the same string with
 * the first character capitalized.
 * @param {string} string - The `string` parameter is a string value that
 * represents the input string that you want to capitalize the first letter of.
 */
export const ucFirst = (string: string) =>
	string.charAt(0).toUpperCase() + string.slice(1);

/**
 * The function `getSiblings` returns an array of all the sibling elements of a
 * given HTML element.
 * @param {HTMLElement} el - el is the HTML element for which we want to find the
 * siblings.
 * @returns The function `getSiblings` returns an array of HTMLElements that are
 * siblings of the input element `el`.
 */
export const getSiblings = (el: HTMLElement) => {
	if (el.parentNode === null) return [];
	return Array.from(el.parentNode.children).filter((child) => child !== el);
};

/**
 * The function `cn` is a TypeScript function that uses the `clsx` library to
 * concatenate and conditionally apply CSS class names.
 * @param {clsx.ClassValue[]} args - The `args` parameter is a rest parameter that
 * allows you to pass in multiple arguments of type `clsx.ClassValue`. These
 * arguments can be any valid class names or class name objects that you want to
 * combine using the `clsx` function.
 */
export const cn = (...args: clsx.ClassValue[]) => clsx(args);

/**
 * The function `extend` takes in a variable number of arguments and returns a
 * composed object using the default values provided.
 * @param {Partial<T>[]} args - An array of objects of type Partial<T>.
 */
export const extend = <T>(...args: Partial<T>[]) => defaultComposer(args);

/**
 * The function converts a string to kebab case by replacing spaces with hyphens
 * and converting camel case to kebab case.
 * @param {string} str - The `str` parameter is a string that you want to convert
 * to kebab case.
 */
export const stringToKebabCase = (str: string) =>
	str
		.replace(/([a-z])([A-Z])/g, "$1-$2")
		.replace(/\s+/g, "-")
		.toLowerCase();

export const moveArrayItem = (array: any[], from: number, to: number) => {
	if (from < 0 || from >= array.length || to < 0 || to >= array.length) {
		console.error("Invalid index");
		return array;
	}

	const itemToMove = array.splice(from, 1)[0];
	array.splice(to, 0, itemToMove);

	return array;
};

export const getFilenameFromUrl = (url: string) =>
	url.substring(url.lastIndexOf("/") + 1);

export const deepClone = <T>(obj: T): T => {
	if (obj === null || typeof obj !== "object") {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => deepClone(item)) as unknown as T;
	}

	const clonedObj = {} as T;

	for (let key in obj) {
		clonedObj[key] = deepClone(obj[key]);
	}

	return clonedObj;
};

export const _set = <T>(
	object: T,
	path: string | Array<string | number>,
	value: any
): T => {
	if (object == null) return object;
	const pathArray = isArray(path) ? path : path.split(".");

	let key = pathArray[0];
	if (pathArray.length === 1) {
		(object as any)[key] = value;
		return object;
	}

	if (!(object as any)[key]) {
		(object as any)[key] = {};
	}

	const nestedObject = (object as any)[key];
	const nestedPath = pathArray.slice(1);

	_set(nestedObject, nestedPath, value);

	return object;
};

export const _get = <T>(
	obj: T,
	path: string | string[],
	defaultValue?: any
): any => {
	const pathArray = Array.isArray(path) ? path : path.split(".");
	let result = obj;
	for (let key of pathArray) {
		if (result == null) {
			return defaultValue;
		}
		result = (result as any)[key];
	}
	return result;
};

export const isEqual = (value: any, other: any): boolean => {
	if (value === other) return true;

	if (repr(value) !== repr(other)) return false;

	if (value instanceof Date && other instanceof Date)
		value.getTime() === other.getTime();

	if (Array.isArray(value) && Array.isArray(other)) {
		if (value.length !== other.length) return false;
		const sortedValue = value.slice().sort();
		const sortedOther = other.slice().sort();
		for (let i = 0; i < sortedValue.length; i++) {
			if (!isEqual(sortedValue[i], sortedOther[i])) return false;
		}
		return true;
	}

	if (value instanceof Object && other instanceof Object) {
		const valueKeys = Object.keys(value);
		const otherKeys = Object.keys(other);
		if (valueKeys.length !== otherKeys.length) return false;
		for (const key of valueKeys) {
			if (!other.hasOwnProperty(key)) return false;
			if (!isEqual(value[key], other[key])) return false;
		}
		return true;
	}

	return false;
};

export const omit = <T extends object, K extends keyof T>(
	obj: T,
	keysToOmit: K[]
): Omit<T, K> => {
	const clonedObj = deepClone(obj);

	keysToOmit.forEach((key) => {
		delete clonedObj[key];
	});

	return clonedObj as Omit<T, K>;
};

export const kebabToCamelCase = (str: string) =>
	str
		.split("-")
		.map((n, idx) => (0 === idx ? n : ucFirst(n)))
		.join("");

export const debounceAsync = <T>(
	func: (...args: any[]) => Promise<any>,
	delayMS: number
): ((...args: any[]) => Promise<T>) => {
	let timeoutId: number | undefined;
	let activePromise: Promise<any> | undefined;

	return async function debounced(...args: any[]) {
		if (!activePromise && !timeoutId) {
			return new Promise((resolve, reject) => {
				activePromise = func(...args)
					.then(resolve)
					.catch(reject)
					.finally(() => {
						activePromise = undefined;
					});
			});
		}

		if (activePromise) {
			await activePromise;
		}

		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}

		return new Promise((resolve, reject) => {
			timeoutId = setTimeout(() => {
				activePromise = func(...args)
					.then(resolve)
					.catch(reject)
					.finally(() => {
						activePromise = undefined;
						timeoutId = undefined;
					});
			}, delayMS);
		});
	};
};

export const colorIsCSSVar = (color?: string) => {
	if (!color) return false;
	return color.startsWith("var(");
};

export const noop = () => {};

export type LayoutGroup = {
	id: string;
	title?: string;
	attrKey: string;
	options?: any[];
	value?: string;
};

/**
 * Build an object that clears all layout advanced-style attributes
 * except `keepAttrKey`. Ready to spread into setAttributes().
 *
 * @param layoutsData - array of layout groups (must include attrKey)
 * @param keepAttrKey - attrKey to keep (do not clear)
 * @param clearValue - value to assign to cleared keys (default: "")
 * @param extraKeys - optional extra attribute keys to also clear
 * @returns object mapping attrKey -> clearValue for all cleared keys
 */
export function clearOtherAdvancedStyles(
	layoutsData: LayoutGroup[],
	keepAttrKey: string,
	clearValue: string | null = "",
	extraKeys: string[] = []
): Record<string, string | null> {
	const clears: Record<string, string | null> = {};

	layoutsData.forEach((ld) => {
		if (ld.attrKey && ld.attrKey !== keepAttrKey) {
			clears[ld.attrKey] = clearValue;
		}
	});

	extraKeys.forEach((k) => {
		if (k !== keepAttrKey) {
			clears[k] = clearValue;
		}
	});

	return clears;
}
